import { useMemo, useCallback } from 'react'

import _ from 'lodash'

import { getConfig } from '@/shared/config/missions'
import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useMissionCapacity } from '@/shared/hooks/useMissionCapacity'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { sortByPosition } from '@/shared/lib/array/sort-by-position'
import type { Mission, MissionSubmission, MissionWithProgress } from '@/types/mission'

import { filterVisibleForGuilds } from '@/shared/lib/guild/visibility'

import { useGamificationDataStore } from './gamificationDataStore'

interface UseMissionsStoreProps {
  id?: number
  uid?: string
}

interface MissionGroups {
  active: MissionWithProgress[]
  pending: MissionWithProgress[]
  approved: MissionWithProgress[]
  closed: MissionWithProgress[]
}

export const useMissionsStore = ({ id, uid } = {} as UseMissionsStoreProps) => {
  const { missions: missionsRaw } = useGamificationDataStore(s =>
    s.getActiveProductData()
  )

  const { digitalProductId } = useDigitalProductContext()

  const { missionSubmissions, missionWasFinished, guildIds } = useUserProgress()
  const {
    missionCapacity,
    getCapacityByMissionId,
    refetchMissionCapacity
  } = useMissionCapacity()

  const config = getConfig({ digitalProductId })

  const getMissionProgress = useCallback(
    (id: number) => {
      const finished = missionWasFinished(id)
      const submissions = _.orderBy(
        missionSubmissions.filter(
          (submission: MissionSubmission) => submission.mission_id == id
        ),
        'id',
        'desc'
      )
      const submission =
        _.find(submissions, { status: 'approved' }) ||
        _.find(submissions, { status: 'pending' }) ||
        _.find(submissions, { status: 'refused' }) ||
        _.get(submissions, '0', undefined)

      return {
        finished,
        submitted: !!submission,
        status: submission?.status,
        submission
      }
    },
    [missionWasFinished, missionSubmissions]
  )

  const missions = useMemo(() => {
    // O bundle traz as missões do evento inteiro; o recorte por guilda é decidido
  // aqui. A imposição de verdade é do servidor, em `missions#finish`.
  const missionsWithProgress = filterVisibleForGuilds(missionsRaw as Mission[], guildIds).map<MissionWithProgress>(
      mission => ({
        ...mission,
        _progress: getMissionProgress(mission.id),
        _capacity: getCapacityByMissionId(mission.id)
      })
    )

    const missionsSortedByPosition = missionsWithProgress
      .slice()
      .sort(sortByPosition)

    const groupedMissions = missionsSortedByPosition.reduce<MissionGroups>(
      (groups, mission) => {
        const status = config.getStatus(mission)
        const isClosed = status?.slug === 'closed'

        if (isClosed) {
          groups.closed.push(mission)
          return groups
        }

        if (mission._progress.status === 'pending') {
          groups.pending.push(mission)
          return groups
        }

        if (mission._progress.status === 'approved') {
          groups.approved.push(mission)
          return groups
        }

        groups.active.push(mission)
        return groups
      },
      { active: [], pending: [], approved: [], closed: [] }
    )

    return [
      ...groupedMissions.active,
      ...groupedMissions.pending,
      ...groupedMissions.approved,
      ...groupedMissions.closed
    ]
  }, [missionsRaw, guildIds, getMissionProgress, getCapacityByMissionId])

  const visibleMissions = missions.filter(mission => !mission.hidden)

  const mission = useMemo(() => {
    if (!id && !uid) return null

    return missions.find(mission => mission.id === id || mission.uid === uid)
  }, [missions, id, uid])

  return {
    missions,
    mission,
    config,

    visibleMissions,

    missionWasFinished,
    getMissionProgress,

    missionCapacity,
    refetchMissionCapacity
  }
}
